import { useApiGet, useApiPost, tokenManager, clearAllCache } from "@ametie/vue-muza-use";
import { useToast } from "@app/core";
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRouter } from "vue-router";

import { useCheckTokenRequest } from "../api/useCheckTokenRequest";
import { useResetPasswordRequest } from "../api/useResetPasswordRequest";
import { UserRole } from "../types";
import type { User, UserPermission, ResetPasswordPayload } from "../types";

// Deep import on purpose — the feature barrel exports PartnershipContextBlock,
// which imports "@/features/auth": a barrel import here would be circular.
import { usePartnershipStore } from "@/features/partnership/store/partnershipStore";
import { useVendorFiltersStore } from "@/shared/stores/useVendorFiltersStore";
import { resolveRedirect } from "@/shared/utils";

export const useAuthStore = defineStore("auth", () => {
  const router = useRouter();
  const { error: toastError, success } = useToast();

  // State
  const user = ref<User | null>(null);
  const isLoading = ref(false);
  const resetPasswordLoading = ref(false);
  const checkTokenLoading = ref(false);
  const isInitialized = ref(false);
  const error = ref<string | null>(null);
  const loginLoading = ref(false);
  const pendingEmail = ref<string | null>(null);

  // Computed
  const isAuthenticated = computed(() => !!user.value);

  const userInitials = computed(() => {
    if (!user.value) return "?";

    const name = user.value.name || user.value.email;
    const parts = name.split(" ");

    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    return name.substring(0, 2).toUpperCase();
  });

  const userDisplayName = computed(() => {
    if (!user.value) return "";
    return user.value.nickname || user.value.name || user.value.email;
  });

  const canViewActiveOnlyToggle = computed(() =>
    user.value?.role === UserRole.ADMIN || user.value?.role === UserRole.INTERNAL,
  );

  /**
     * Check if user has specific permission
     */
  const hasPermission = (permission: UserPermission): boolean => {
    return user.value?.permissions.includes(permission) ?? false;
  };

  /**
     * Check if user has any of the specified permissions
     */
  const hasAnyPermission = (permissions: UserPermission[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  /**
     * Check if user has all of the specified permissions
     */
  const hasAllPermissions = (permissions: UserPermission[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };

  const { loading: initLoading, error: initError, execute } = useApiGet<{ user: User }>("/auth/users/me", {
    onSuccess: ({ data }) => {
      user.value = data.user;
    },
  });

  const initialize = async () => {
    if (user.value || isInitialized.value) {
      return;
    }
    await execute();
  };

  const { error: loginError, execute: loginExecute } = useApiPost<{
    access_token: string
    must_change_password: boolean
  }>("/auth/login", {
    onSuccess: async ({ data }) => {
      const { redirect } = router.currentRoute.value.query;

      if (data.must_change_password) {
        await router.push({
          path: "/password-change",
          query: typeof redirect === "string" ? { redirect } : undefined,
        });
        return;
      }
      tokenManager.setTokens({ accessToken: data.access_token });

      loginLoading.value = false;
      await initialize();

      await router.push(resolveRedirect(redirect));
    },
    onError: () => {
      loginLoading.value = false;
    },
    authMode: "public",
    withCredentials: true,
  });

  const login = async (email: string, password: string): Promise<void> => {
    loginLoading.value = true;
    pendingEmail.value = email;
    await loginExecute({
      data: {
        email,
        password,
      },
    });
  };

  /**
     * Logout user
     */
  const logout = async (): Promise<void> => {
    isLoading.value = true;

    try {
      // await mockLogout();
    }
    catch (err) {
      console.error("Logout error:", err);
    }
    finally {
      tokenManager.clearTokens();
      user.value = null;
      usePartnershipStore().reset();
      useVendorFiltersStore().reset(); // vendor brands/products are user-scoped
      clearAllCache(); // in-memory response cache (e.g. /vendors) is user-scoped
      isLoading.value = false;
      await router.push("/login");
    }
  };

  /**
     * Check reset password token validity
     */
  const checkToken = async (token: string, callback?: () => void) => {
    checkTokenLoading.value = true;
    const { execute, data, error } = useCheckTokenRequest(token);

    await execute();

    if (error.value) {
      toastError(error.value.message || "Invalid token");
      callback?.();
    }
    else if (data.value && !data.value.isValid) {
      toastError("Invalid token");
      callback?.();
    }
    checkTokenLoading.value = false;
  };

  /**
     * Reset password
     */
  const resetPassword = async (
    token: string,
    payload: ResetPasswordPayload,
    callback?: () => void,
  ) => {
    resetPasswordLoading.value = true;
    const { execute, error } = useResetPasswordRequest(token);

    await execute({
      data: payload,
    });

    if (error.value) {
      toastError(error.value.message);
    }
    else {
      success("Password reset successfully");
      callback?.();
    }
    resetPasswordLoading.value = false;
  };

  /**
     * Send reset password link to email
     */
  const getResetPasswordLink = async (
    email: string,
    callback?: () => void,
    isForgotPassword?: boolean,
    onErrorCallback?: () => void,
  ) => {
    isLoading.value = true;

    const { execute } = useApiPost<{ message: string }>("/auth/send-reset-password-email", {
      authMode: "public",
      skipErrorNotification: true,
      onSuccess: () => {
        if (isForgotPassword) {
          success("Reset password link sent to your email");
        }
        callback?.();
      },
      onError: (err) => {
        toastError(err.message || "Failed to send reset password link");
        onErrorCallback?.();
      },
    });

    await execute({
      data: { email },
    });

    isLoading.value = false;
  };

  return {
    // State
    user,
    pendingEmail,
    loginLoading,
    resetPasswordLoading,
    checkTokenLoading,
    loginError,
    initError,
    initLoading,
    isInitialized,
    error,

    // Computed
    isAuthenticated,
    userInitials,
    userDisplayName,
    canViewActiveOnlyToggle,

    // Actions
    initialize,
    login,
    logout,
    checkToken,
    resetPassword,
    getResetPasswordLink,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
});
